import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Check,
  CreditCard,
  Zap,
  Users,
  FolderOpen,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  AlertTriangle,
} from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useUpgrade } from "../contexts/UpgradeContext";
import { useNavigation } from "../contexts/NavigationContext";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

let stripePromiseCache: { [accountId: string]: Promise<any> } = {};

const getStripe = async (accountId?: string, forceReload: boolean = false) => {
  const cacheKey = accountId || 'default';
  
  // Reset cache if force reload
  if (forceReload) {
    delete stripePromiseCache[cacheKey];
  }
  
  if (!stripePromiseCache[cacheKey]) {
    try {
      const token = localStorage.getItem("token");
      const url = new URL(`${apiUrl}/api/stripe/config`);
      if (accountId) {
        url.searchParams.append('accountId', accountId);
      }
      
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch Stripe config: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to load Stripe configuration");
      }

      if (!data.data?.publishableKey) {
        throw new Error(
          "Stripe publishable key is missing. Please check your server configuration.",
        );
      }

      console.log(`🔑 [STRIPE] Loading Stripe with ${data.data.mode} mode key for account ${accountId || 'default'}`);
      
      stripePromiseCache[cacheKey] = loadStripe(data.data.publishableKey);
    } catch (error) {
      console.error("Failed to load Stripe config:", error);
      throw error; // Re-throw to allow caller to handle
    }
  }
  return stripePromiseCache[cacheKey];
};

interface PaymentFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

interface PaymentMethod {
  id: string;
  type: "card";
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const cardElementRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [seats, setSeats] = useState(1);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">(
    "month",
  );
  const [pricing, setPricing] = useState<{
    monthly: number;
    annual: number;
  } | null>(null);
  const [stripeMode, setStripeMode] = useState<'test' | 'live' | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);

  // Fetch saved payment methods on mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoadingPaymentMethods(true);
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/stripe/payment-methods`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && data.data.length > 0) {
            setPaymentMethods(data.data);
            // Auto-select default payment method if available
            const defaultMethod = data.data.find(
              (pm: PaymentMethod) => pm.isDefault,
            );
            if (defaultMethod) {
              setSelectedPaymentMethodId(defaultMethod.id);
              setUseNewCard(false);
            } else {
              setUseNewCard(true);
            }
          } else {
            setUseNewCard(true);
          }
        } else {
          setUseNewCard(true);
        }
      } catch (err) {
        console.error("Failed to fetch payment methods:", err);
        setUseNewCard(true);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  // Update card element disabled state when stripe or loading changes
  useEffect(() => {
    const cardElement = elements?.getElement(CardElement);
    if (cardElement) {
      cardElement.update({ disabled: !stripe || loading || !useNewCard });
    }
  }, [stripe, loading, elements, useNewCard]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate payment method selection
    if (!useNewCard && !selectedPaymentMethodId) {
      onError("Please select a payment method.");
      return;
    }

    // If using new card, Stripe and elements must be ready
    if (useNewCard && (!stripe || !elements)) {
      onError("Payment system not ready. Please try again.");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required. Please log in again.");
      }

      let paymentMethodId: string | null = null;

      // If using saved payment method, use it directly
      if (!useNewCard && selectedPaymentMethodId) {
        paymentMethodId = selectedPaymentMethodId;
      } else {
        // Validate Stripe and elements are available for new card
        if (!stripe || !elements) {
          throw new Error("Payment system not ready. Please try again.");
        }

        // Create setup intent for new payment method with timeout
        const setupController = new AbortController();
        const setupTimeout = setTimeout(() => setupController.abort(), 30000); // 30 second timeout

        let setupResponse;
        try {
          setupResponse = await fetch(`${apiUrl}/api/stripe/setup-intent`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal: setupController.signal,
          });
        } catch (fetchError: any) {
          clearTimeout(setupTimeout);
          if (fetchError.name === "AbortError") {
            throw new Error(
              "Request timed out. Please check your connection and try again.",
            );
          }
          throw fetchError;
        }
        clearTimeout(setupTimeout);

        if (!setupResponse.ok) {
          const errorData = await setupResponse
            .json()
            .catch(() => ({ message: "Failed to create setup intent" }));
          throw new Error(
            errorData.message || `Server error: ${setupResponse.status}`,
          );
        }

        const setupData = await setupResponse.json();

        if (!setupData.success) {
          throw new Error(setupData.message || "Failed to create setup intent");
        }

        // Confirm card setup
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error("Card element not found");
        }

        const { setupIntent, error: stripeError } =
          await stripe.confirmCardSetup(setupData.data.clientSecret, {
            payment_method: {
              card: cardElement,
            },
          });

        if (stripeError) {
          throw new Error(stripeError.message);
        }

        if (setupIntent?.status !== "succeeded") {
          throw new Error("Card setup failed");
        }

        paymentMethodId = setupIntent.payment_method as string;
      }

      // Now upgrade the subscription with the payment method with timeout
      const upgradeController = new AbortController();
      const upgradeTimeout = setTimeout(() => upgradeController.abort(), 60000); // 60 second timeout

      let upgradeResponse;
      try {
        upgradeResponse = await fetch(`${apiUrl}/api/subscriptions/upgrade`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            seats,
            paymentMethodId,
            billingInterval,
          }),
          signal: upgradeController.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(upgradeTimeout);
        if (fetchError.name === "AbortError") {
          throw new Error(
            "Upgrade request timed out. Please check your connection and try again.",
          );
        }
        throw fetchError;
      }
      clearTimeout(upgradeTimeout);

      if (!upgradeResponse.ok) {
        const errorData = await upgradeResponse
          .json()
          .catch(() => ({ message: "Failed to upgrade subscription" }));
        throw new Error(
          errorData.message || `Server error: ${upgradeResponse.status}`,
        );
      }

      const upgradeData = await upgradeResponse.json();

      if (!upgradeData.success) {
        throw new Error(
          upgradeData.message || "Failed to upgrade subscription",
        );
      }

      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to process payment");
    } finally {
      setLoading(false);
    }
  };

  // Fetch pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const token = localStorage.getItem("token");
        
        // Decode token to get accountId
        const payload = JSON.parse(atob(token!.split('.')[1]));
        const accountId = payload.currentAccountId;
        
        const url = new URL(`${apiUrl}/api/stripe/config`);
        if (accountId) {
          url.searchParams.append('accountId', accountId);
        }
        
        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          if (data.data?.pricing) {
            // Use centralized pricing from API
            setPricing({
              monthly: data.data.pricing.monthly || 30,
              annual: data.data.pricing.annual || 300,
            });
          } else {
            // Fallback to defaults if pricing not available
            setPricing({ monthly: 30, annual: 300 });
          }
          // Store mode for display
          if (data.data?.mode) {
            setStripeMode(data.data.mode);
          }
        } else {
          // Fallback to defaults if pricing not available
          setPricing({ monthly: 30, annual: 300 });
        }
      } catch (err) {
        console.error("Failed to fetch pricing:", err);
        setPricing({ monthly: 30, annual: 300 }); // Fallback
      }
    };
    fetchPricing();
  }, []);

  const monthlyPrice = pricing?.monthly || 30;
  const annualPrice = pricing?.annual || 300;
  const monthlyCost =
    billingInterval === "year"
      ? (seats * annualPrice) / 12
      : seats * monthlyPrice;
  const totalCost =
    billingInterval === "year" ? seats * annualPrice : seats * monthlyPrice;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Stripe Mode Indicator */}
      {stripeMode === 'test' && (
        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                Test Mode Active
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                You're in test mode. Use test card <code className="bg-orange-100 dark:bg-orange-900/40 px-1 rounded">4242 4242 4242 4242</code> with any future expiry date and CVC.
              </p>
            </div>
          </div>
        </div>
      )}
      {stripeMode === 'live' && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-sm font-medium text-green-800 dark:text-green-300">
              Live Mode - Real payments will be processed
            </p>
          </div>
        </div>
      )}

      {/* Billing Interval Toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Billing Cycle
        </label>
        <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
          <button
            type="button"
            onClick={() => setBillingInterval("month")}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              billingInterval === "month"
                ? "bg-accent-600 text-white"
                : "bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval("year")}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              billingInterval === "year"
                ? "bg-accent-600 text-white"
                : "bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500"
            }`}
          >
            Annual
          </button>
        </div>
        {billingInterval === "year" && (
          <p className="text-xs text-success-600 dark:text-success-400 mt-1">
            Save ~17% with annual billing
          </p>
        )}
      </div>

      {/* Seats Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Number of Seats
        </label>
        <input
          type="number"
          min="1"
          value={seats}
          onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          {billingInterval === "year" ? (
            <>
              ${totalCost.toFixed(2)}/year (${monthlyCost.toFixed(2)}/month)
            </>
          ) : (
            <>${totalCost.toFixed(2)}/month</>
          )}
        </p>
      </div>

      {/* Payment Method Selection */}
      {loadingPaymentMethods ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
            Loading payment methods...
          </span>
        </div>
      ) : paymentMethods.length > 0 ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Payment Method
          </label>

          {/* Saved Payment Methods */}
          <div className="space-y-2 mb-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => {
                  setSelectedPaymentMethodId(method.id);
                  setUseNewCard(false);
                }}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  !useNewCard && selectedPaymentMethodId === method.id
                    ? "border-primary-600 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                          {method.brand} •••• {method.last4}
                        </span>
                        {method.isDefault && (
                          <span className="text-xs px-2 py-0.5 bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Expires {method.expiryMonth.toString().padStart(2, "0")}
                        /{method.expiryYear}
                      </p>
                    </div>
                  </div>
                  {!useNewCard && selectedPaymentMethodId === method.id && (
                    <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Use New Card Option */}
          <div
            onClick={() => {
              setUseNewCard(true);
              setSelectedPaymentMethodId(null);
            }}
            className={`p-3 border-2 rounded-lg cursor-pointer transition-colors mb-4 ${
              useNewCard
                ? "border-primary-600 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plus className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Use a new card
                </span>
              </div>
              {useNewCard && (
                <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Card Information (only show if using new card) */}
      {useNewCard && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Card Information
          </label>
          <div
            className={`p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 ${!stripe || loading ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
          >
            <CardElement
              onReady={(element) => {
                cardElementRef.current = element;
                // Set initial disabled state
                if (!stripe || loading) {
                  element.update({ disabled: true });
                }
              }}
              options={{
                disabled: !stripe || loading,
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </div>
          {(!stripe || loading) && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {!stripe ? "Payment system loading..." : "Processing payment..."}
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Upgrade to Premium
          </>
        )}
      </button>

      <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
        Secure payment processed by Stripe. You can cancel anytime.
      </p>
    </form>
  );
};

export const UpgradeModal: React.FC = () => {
  const {
    isUpgradeModalOpen,
    returnPath,
    limitType,
    limitDetails,
    closeUpgradeModal,
    completeUpgrade,
  } = useUpgrade();
  const { navigate } = useNavigation();
  const [step, setStep] = useState<"benefits" | "payment" | "success">(
    "benefits",
  );
  const [error, setError] = useState<string | null>(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<any>(null);
  const [pricing, setPricing] = useState<{
    monthly: number;
    annual: number;
  } | null>(null);

  useEffect(() => {
    if (isUpgradeModalOpen) {
      initializeStripe();
      setStep("benefits");
      setError(null);
      setStripeError(null);
      fetchPricing();
    }
  }, [isUpgradeModalOpen]);

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Decode token to get accountId
      const payload = JSON.parse(atob(token!.split('.')[1]));
      const accountId = payload.currentAccountId;
      
      const url = new URL(`${apiUrl}/api/stripe/config`);
      if (accountId) {
        url.searchParams.append('accountId', accountId);
      }
      
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && data.data?.pricing) {
        setPricing({
          monthly: data.data.pricing.monthly || 30,
          annual: data.data.pricing.annual || 300,
        });
      } else {
        setPricing({ monthly: 30, annual: 300 });
      }
    } catch (err) {
      console.error("Failed to fetch pricing:", err);
      setPricing({ monthly: 30, annual: 300 });
    }
  };

  const monthlyPrice = pricing?.monthly || 30;
  const annualPrice = pricing?.annual || 300;

  const initializeStripe = async () => {
    try {
      setStripeLoaded(false);
      setStripeError(null);
      
      const token = localStorage.getItem("token");
      if (token) {
        // Decode token to get accountId
        const payload = JSON.parse(atob(token.split('.')[1]));
        const accountId = payload.currentAccountId;
        
        // Force reload Stripe instance to ensure we get the correct mode
        const stripe = await getStripe(accountId, true);
        setStripeInstance(stripe);
      }
      setStripeLoaded(true);
    } catch (err) {
      console.error("Stripe initialization error:", err);
      setStripeError(
        err instanceof Error
          ? err.message
          : "Failed to initialize payment system",
      );
      setStripeLoaded(true); // Set to true to show error message instead of infinite loading
    }
  };

  if (!isUpgradeModalOpen) {
    return null;
  }

  const getLimitMessage = () => {
    if (!limitType) return null;

    const messages = {
      staff: {
        title: "Staff Limit Reached",
        description: `You've reached the free plan limit of ${limitDetails?.max || 3} staff members.`,
      },
      projects: {
        title: "Project Limit Reached",
        description: `You've reached the free plan limit of ${limitDetails?.max || 3} projects.`,
      },
      billExtractions: {
        title: "AI Extraction Limit Reached",
        description: `You've used all ${limitDetails?.max || 10} AI bill extractions this month.`,
      },
      integrations: {
        title: "Integration Limit Reached",
        description: `You've reached the free plan limit of ${limitDetails?.max || 2} integrations.`,
      },
    };

    return messages[limitType];
  };

  const limitMessage = getLimitMessage();

  const premiumFeatures = [
    {
      icon: Users,
      title: "Unlimited Staff",
      description: "Add as many team members as you need",
    },
    {
      icon: FolderOpen,
      title: "Unlimited Projects",
      description: "Create and manage unlimited projects",
    },
    {
      icon: FileText,
      title: "Unlimited AI Extractions",
      description: "Process bills with AI without limits",
    },
    {
      icon: Zap,
      title: "All Integrations",
      description: "Connect to Xero, Gmail, Outlook, and more",
    },
  ];

  const handlePaymentSuccess = () => {
    setStep("success");
    setTimeout(() => {
      completeUpgrade();
      if (returnPath) {
        navigate(returnPath);
      }
    }, 2000);
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleClose = () => {
    // Allow closing at any time - user should be able to cancel
    closeUpgradeModal();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {step === "benefits" && "Upgrade to Premium"}
            {step === "payment" && "Complete Your Upgrade"}
            {step === "success" && "Welcome to Premium!"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Step 1: Benefits */}
          {step === "benefits" && (
            <div className="space-y-6">
              {/* Limit Alert */}
              {limitMessage && (
                <div className="bg-amber-50 dark:bg-gray-700 dark:border dark:border-amber-800/50 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                        {limitMessage.title}
                      </h3>
                      <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                        {limitMessage.description}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg p-6 border-2 border-primary-200 dark:border-primary-700">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    ${monthlyPrice.toFixed(0)}
                    <span className="text-xl text-gray-600 dark:text-gray-400">
                      /seat/month
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Cancel anytime. No long-term commitment.
                  </p>
                  {pricing && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Annual billing: ${annualPrice.toFixed(0)}/seat/year (save
                      ~17%)
                    </p>
                  )}
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {premiumFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                        <feature.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => {
                  setError(null);
                  setStep("payment");
                }}
                className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold flex items-center justify-center"
              >
                Continue to Payment
              </button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === "payment" && (
            <div className="space-y-6">
              {(error || stripeError) && (
                <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-error-900 dark:text-error-100">
                        {stripeError
                          ? "Payment System Error"
                          : "Payment Failed"}
                      </h3>
                      <p className="text-sm text-error-800 dark:text-error-300 mt-1">
                        {stripeError || error}
                      </p>
                      {stripeError && (
                        <button
                          onClick={initializeStripe}
                          className="mt-2 text-sm text-error-700 dark:text-error-300 underline hover:no-underline"
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!stripeLoaded ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                </div>
              ) : stripeError ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">
                    Please try again or contact support if the problem persists.
                  </p>
                </div>
              ) : stripeInstance ? (
                <Elements stripe={stripeInstance}>
                  <PaymentForm
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                </Elements>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">
                    Payment system not available. Please try again later.
                  </p>
                </div>
              )}

              <button
                onClick={() => setStep("benefits")}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Back
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === "success" && (
            <div className="text-center py-12 space-y-6">
              <div className="flex justify-center">
                <div className="p-4 bg-success-100 dark:bg-success-900/30 rounded-full">
                  <CheckCircle className="w-16 h-16 text-success-600 dark:text-success-400" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Upgrade Successful!
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  You now have access to all Premium features. Redirecting you
                  back...
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
